import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { addNewsletterSubscriptionToBrevo } from '../services/brevoService';

const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionEmail, setSubscriptionEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [subscriptionSuccess, setSubscriptionSuccess] = useState(false);

  // EmailJS configuration for newsletter subscription
  const EMAILJS_SERVICE_ID = "service_q9czpsy";
  const EMAILJS_NEWSLETTER_TEMPLATE_ID = "template_newsletter"; // New template for newsletter
  const EMAILJS_PUBLIC_KEY = "CrUwO8SVJ5ZJulRFw";

  useEffect(() => {
    // Initialize EmailJS
    emailjs.init(EMAILJS_PUBLIC_KEY);
    
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      // Update active section based on scroll position
      const sections = ['hero', 'what-is-vbc', 'who-we-are', 'contact'];
      const currentSection = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      
      if (currentSection) {
        setActiveSection(currentSection);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const validateEmail = (email) => {
    // Regular expression for email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e) => {
    const email = e.target.value;
    setSubscriptionEmail(email);

    // Real-time validation
    if (email === '') {
      setEmailError('');
      setIsEmailValid(false);
    } else if (!validateEmail(email)) {
      setEmailError('Please enter a valid email address');
      setIsEmailValid(false);
    } else {
      setEmailError('');
      setIsEmailValid(true);
    }
  };

  const handleSubscription = async (e) => {
    e.preventDefault();
    
    // Final validation check
    if (!isEmailValid || subscriptionEmail === '') {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    setIsSubmitting(true);

    try {
      const templateParams = {
        to_email: 'sg@vbcollective.xyz',
        subscriber_email: subscriptionEmail,
        subscription_date: new Date().toLocaleDateString(),
      };

      console.log('Processing newsletter subscription...');

      // Send email via EmailJS and add to Brevo simultaneously
      const [emailResult, brevoResult] = await Promise.allSettled([
        emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_NEWSLETTER_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY),
        addNewsletterSubscriptionToBrevo(subscriptionEmail)
      ]);
      
      // Check EmailJS result
      if (emailResult.status === 'fulfilled') {
        console.log('Newsletter notification sent via EmailJS');
      } else {
        console.warn('EmailJS failed:', emailResult.reason);
      }
      
      // Check Brevo result
      if (brevoResult.status === 'fulfilled') {
        console.log('Newsletter subscriber added to Brevo successfully');
      } else {
        console.warn('Brevo integration failed:', brevoResult.reason);
      }
      
      // Success if at least Brevo worked (newsletter is more important than notification)
      if (brevoResult.status === 'fulfilled') {
        setSubscriptionSuccess(true);
        setSubscriptionEmail('');
        setEmailError('');
        setIsEmailValid(false);
        
        // Hide success message and modal after 3 seconds
        setTimeout(() => {
          setSubscriptionSuccess(false);
          setShowSubscriptionModal(false);
        }, 3000);
      } else {
        throw new Error('Failed to add subscriber to newsletter list');
      }

    } catch (error) {
      console.error('Error subscribing to newsletter:', error);
      
      // More specific error handling
      let errorMessage = 'Failed to subscribe. Please try again.';
      
      if (error.message) {
        if (error.message.includes('template')) {
          errorMessage = 'Email template not configured, but you\'ve been added to our newsletter list.';
        } else if (error.message.includes('service')) {
          errorMessage = 'Email service not available. Please try again later.';
        }
      }
      
      alert(errorMessage + ' You can also email us directly at info@vbcollective.xyz');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openSubscriptionModal = () => {
    setShowSubscriptionModal(true);
    // Reset form state
    setSubscriptionEmail('');
    setEmailError('');
    setIsEmailValid(false);
    setSubscriptionSuccess(false);
  };

  const navItems = [
    { id: 'hero', label: 'Home' },
    { id: 'what-is-vbc', label: 'What is VBC' },
    { id: 'who-we-are', label: 'Who We Are' },
    { id: 'contact', label: 'Contact' },
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? 'glass-dark shadow-xl border-b border-white/10' 
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="flex items-center space-x-3"
          >
            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-lg">
              <img 
                src="/vbc-logo.svg" 
                alt="VBC Logo" 
                className="w-8 h-8 object-contain"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <span className="text-navy-900 font-bold text-lg hidden">V</span>
            </div>
            <span className="text-xl font-bold text-white">VBC</span>
          </motion.div>

          {/* Navigation Links */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item, index) => (
              <motion.button
                key={item.id}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                onClick={() => scrollToSection(item.id)}
                className={`relative px-4 py-2 text-sm font-medium transition-all duration-300 ${
                  activeSection === item.id
                    ? 'text-secondary-400'
                    : 'text-gray-300 hover:text-white'
                }`}
              >
                {item.label}
                {activeSection === item.id && (
                  <motion.div
                    layoutId="activeIndicator"
                    className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-secondary-400 to-primary-400"
                    initial={false}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </motion.button>
            ))}
          </div>

          {/* CTA Button */}
          <motion.button
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            onClick={openSubscriptionModal}
            className="btn-primary text-sm hidden md:block"
          >
            Subscribe to VBC Newsletter
          </motion.button>
        </div>
      </div>

      {/* Newsletter Subscription Modal */}
      {showSubscriptionModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] px-4 overflow-y-auto">
          <div className="min-h-screen flex items-center justify-center py-8 w-full">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="glass-dark rounded-2xl p-8 max-w-md w-full border border-white/10 relative my-auto"
            >
              {/* Close Button */}
              <button
                onClick={() => setShowSubscriptionModal(false)}
                className="absolute top-4 right-4 w-8 h-8 bg-white/10 rounded-lg flex items-center justify-center text-gray-400 hover:text-white hover:bg-white/20 transition-all duration-300 border border-white/20 z-10"
                aria-label="Close"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {!subscriptionSuccess ? (
                <>
                  <div className="text-center mb-6 pr-8">
                    <h3 className="text-2xl font-bold text-white mb-2">
                      Subscribe to VBC Newsletter
                    </h3>
                    <p className="text-gray-300 text-sm">
                      Stay updated with our latest insights, portfolio updates, and investment opportunities.
                    </p>
                  </div>

                  <form onSubmit={handleSubscription} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        required
                        value={subscriptionEmail}
                        onChange={handleEmailChange}
                        className={`w-full px-4 py-3 bg-white/5 border rounded-lg text-white placeholder-gray-400 transition-all duration-300 ${
                          emailError 
                            ? 'border-red-500 focus:border-red-500 focus:ring-1 focus:ring-red-500' 
                            : isEmailValid 
                              ? 'border-green-500 focus:border-green-500 focus:ring-1 focus:ring-green-500'
                              : 'border-white/10 focus:border-secondary-400 focus:ring-1 focus:ring-secondary-400'
                        }`}
                        placeholder="your@email.com"
                      />
                      {emailError && (
                        <p className="mt-2 text-sm text-red-400 flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          {emailError}
                        </p>
                      )}
                      {isEmailValid && !emailError && (
                        <p className="mt-2 text-sm text-green-400 flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          Valid email address
                        </p>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting || !isEmailValid || subscriptionEmail === ''}
                      className={`w-full text-sm py-3 px-4 rounded-xl font-semibold transition-all duration-300 ${
                        isSubmitting || !isEmailValid || subscriptionEmail === ''
                          ? 'bg-gray-600 text-gray-400 cursor-not-allowed' 
                          : 'bg-gradient-to-r from-secondary-500 to-secondary-600 text-white hover:shadow-xl transform hover:scale-105'
                      }`}
                    >
                      {isSubmitting ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                          Subscribing...
                        </div>
                      ) : (
                        'Subscribe'
                      )}
                    </button>
                  </form>
                </>
              ) : (
                <div className="text-center py-8 pr-8">
                  <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-green-400 text-2xl">✓</span>
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2">
                    Thanks for subscribing to VBC!
                  </h3>
                  <p className="text-gray-300 text-sm">
                    You'll receive our latest updates and insights.
                  </p>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      )}
    </motion.nav>
  );
};

export default Navigation;